import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/auth-context";
import { useTheme } from "../context/ThemeContext";
import logviComplete from "../assets/icons/logvi-complete.png";

/** Resolver de avatar para CRA/Webpack */
const profileCtx: any = (require as any).context(
  "../assets/profile",
  false,
  /\.(jpe?g|png)$/i
);

const API_BASE = "https://api.logvi-havox.com";

function hashIndex(seed: string): number {
  let h = 0;

  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  }

  return h % 6;
}

function resolveProfileUrl(
  pictureId?: string | number,
  fallbackSeed?: string
): string | undefined {
  if (pictureId !== undefined && pictureId !== null) {
    const id = String(pictureId).trim();
    const candidates = [`./${id}.jpg`, `./${id}.jpeg`, `./${id}.png`];

    for (const c of candidates) {
      try {
        return profileCtx(c) as string;
      } catch {}
    }
  }

  const seed = String(pictureId ?? fallbackSeed ?? "default");
  const idx = hashIndex(seed) + 1;

  const fallbackCandidates = [
    `./profile-${idx}.jpg`,
    `./profile-${idx}.jpeg`,
    `./profile-${idx}.png`,
  ];

  for (const c of fallbackCandidates) {
    try {
      return profileCtx(c) as string;
    } catch {}
  }

  return undefined;
}

function getInitials(fullName: string) {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const second = parts.length > 1 ? parts[1]?.[0] ?? "" : "";

  return (first + second).toUpperCase();
}

function getDisplayName(fullName?: string | null) {
  const clean = String(fullName || "").trim();

  if (!clean) return "Usuário";

  const parts = clean.split(/\s+/).filter(Boolean);

  if (parts.length >= 2) {
    return `${parts[0]} ${parts[1]}`;
  }

  return parts[0];
}

function formatCnpj(cnpj?: string | null) {
  const digits = String(cnpj || "").replace(/\D/g, "");

  if (digits.length !== 14) return cnpj || "";

  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(
    5,
    8
  )}/${digits.slice(8, 12)}-${digits.slice(12, 14)}`;
}

const HeaderHome: React.FC = () => {
  const navigate = useNavigate();
  const { sessao, logout } = useAuth();
  const { systemTitle } = useTheme();

  const menuRef = useRef<HTMLDivElement | null>(null);

  const [menuOpen, setMenuOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [userAreaHovered, setUserAreaHovered] = useState(false);

  const nome = sessao?.nome || "";
  const departamento = sessao?.departamento || "Departamento não identificado";
  const empresaRazaoSocial = sessao?.empresa_razao_social || "ERRO INTERNO";
  const empresaCnpj = sessao?.empresa_cnpj || "";
  const pictureId = sessao?.picture != null ? String(sessao.picture) : "";

  const displayName = useMemo(() => getDisplayName(nome), [nome]);

  const formattedCnpj = useMemo(() => formatCnpj(empresaCnpj), [empresaCnpj]);

  const profileUrl = useMemo(
    () => resolveProfileUrl(pictureId, nome),
    [pictureId, nome]
  );

  const hasName = !!nome;

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!menuOpen) return;

      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }

    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);

    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [menuOpen]);

  const doLogout = async () => {
    if (loggingOut) return;

    setLoggingOut(true);
    setMenuOpen(false);

    try {
      try {
        await fetch(`${API_BASE}/auth/logout`, {
          method: "POST",
          credentials: "include",
        });
      } catch {}

      try {
        localStorage.removeItem("access_token");
        sessionStorage.removeItem("github_oauth_state");
      } catch {}

      await logout();

      navigate("/login", { replace: true });
    } finally {
      setLoggingOut(false);
    }
  };

  return (
    <motion.header
      style={styles.container}
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 260, damping: 26 }}
    >
      <motion.div
        initial={{ opacity: 0, x: -8 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
        style={styles.leftBlock}
      >
        <button
          type="button"
          style={styles.systemInfo}
          onClick={() => navigate("/acess/home")}
        >
          <img
            src={logviComplete}
            alt="Logvi"
            style={styles.systemLogo}
            draggable={false}
          />

          <span style={styles.separator}>|</span>

          <span style={styles.systemTitle}>{systemTitle || "Sistema"}</span>

          <span style={styles.separator}>|</span>

          <span style={styles.companyInfo}>
            {empresaRazaoSocial}
            {formattedCnpj ? ` ${formattedCnpj}` : ""}
          </span>
        </button>
      </motion.div>

      <div style={styles.userInfo} ref={menuRef}>
        <motion.button
          type="button"
          style={{
            ...styles.userTrigger,
            ...(userAreaHovered ? styles.userTriggerHover : {}),
            ...(menuOpen ? styles.userTriggerOpen : {}),
          }}
          onMouseEnter={() => setUserAreaHovered(true)}
          onMouseLeave={() => setUserAreaHovered(false)}
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          whileHover={{ y: -0.5 }}
          whileTap={{ scale: 0.985 }}
          transition={{ type: "spring", stiffness: 320, damping: 24 }}
        >
          <span style={styles.avatarRing}>
            {profileUrl ? (
              <img
                src={profileUrl}
                alt={displayName}
                style={styles.avatar}
                draggable={false}
                loading="lazy"
              />
            ) : (
              <span style={{ ...styles.avatar, ...styles.avatarFallback }}>
                {hasName ? getInitials(nome) : ""}
              </span>
            )}
          </span>

          <span style={styles.userTextBlock}>
            <span
              style={{
                ...styles.name,
                color: userAreaHovered || menuOpen ? "#0F172A" : "#FFFFFF",
              }}
            >
              {displayName}
            </span>
          </span>

          <span
            style={{
              ...styles.chevron,
              transform: menuOpen ? "rotate(180deg)" : "rotate(0deg)",
              color: userAreaHovered || menuOpen ? "#0F172A" : "#FFFFFF",
            }}
            aria-hidden="true"
          >
            <svg width="14" height="14" viewBox="0 0 24 24">
              <path
                d="M6 9l6 6 6-6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </motion.button>

        <AnimatePresence>
          {menuOpen && (
            <motion.div
              style={styles.menu}
              role="menu"
              initial={{ opacity: 0, scale: 0.96, y: -6 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98, y: -6 }}
              transition={{ type: "spring", stiffness: 260, damping: 22 }}
            >
              <div style={styles.accountHeader}>
                <span style={styles.accountAvatarLargeWrap}>
                  {profileUrl ? (
                    <img
                      src={profileUrl}
                      alt={displayName}
                      style={styles.accountAvatarLarge}
                      draggable={false}
                      loading="lazy"
                    />
                  ) : (
                    <span
                      style={{
                        ...styles.accountAvatarLarge,
                        ...styles.accountAvatarFallbackLarge,
                      }}
                    >
                      {hasName ? getInitials(nome) : ""}
                    </span>
                  )}
                </span>

                <div style={styles.accountIdentity}>
                  <span style={styles.accountName}>{displayName}</span>
                  <span style={styles.accountRole}>{departamento}</span>
                </div>
              </div>

              <div style={styles.accountDetails}>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Empresa</span>
                  <span style={styles.detailValue}>{empresaRazaoSocial}</span>
                </div>

                {formattedCnpj && (
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>CNPJ</span>
                    <span style={styles.detailValue}>{formattedCnpj}</span>
                  </div>
                )}

                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Depto.</span>
                  <span style={styles.detailValue}>{departamento}</span>
                </div>
              </div>

              <div style={styles.logoutArea}>
                <button
                  type="button"
                  style={{
                    ...styles.logoutButton,
                    ...(loggingOut ? styles.logoutButtonDisabled : {}),
                  }}
                  onClick={doLogout}
                  disabled={loggingOut}
                >
                  <span style={styles.logoutIcon} aria-hidden="true">
                    {loggingOut ? (
                      <span style={styles.spinner} />
                    ) : (
                      <svg width="17" height="17" viewBox="0 0 24 24">
                        <path
                          d="M10 6H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h4"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <path
                          d="M14 16l4-4-4-4"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8 12h10"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    )}
                  </span>

                  <span>
                    {loggingOut ? "Encerrando sessão..." : "Encerrar sessão"}
                  </span>
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    width: "100%",
    height: "72px",
    minHeight: "72px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 24px",
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 999,
    boxSizing: "border-box",
    background: "transparent",
    border: "none",
    boxShadow: "none",
    overflow: "visible",
    pointerEvents: "none",
  },

  leftBlock: {
    display: "flex",
    alignItems: "center",
    marginRight: 18,
    flex: 1,
    minWidth: 0,
    pointerEvents: "auto",
  },

  systemInfo: {
    margin: 0,
    padding: 0,
    border: "none",
    background: "transparent",
    color: "#FFFFFF",
    fontFamily: '"Nunito", "Inter", sans-serif',
    display: "flex",
    alignItems: "center",
    gap: 10,
    minWidth: 0,
    maxWidth: "100%",
    textShadow: "0 2px 10px rgba(0,0,0,0.18)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    cursor: "pointer",
    userSelect: "none",
  },

  systemLogo: {
    width: 44,
    height: 44,
    objectFit: "contain",
    flexShrink: 0,
    display: "block",
    marginTop: 5,
    pointerEvents: "none",
  },

  systemTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "#FFFFFF",
    whiteSpace: "nowrap",
    textTransform: "uppercase",
    flexShrink: 0,
  },

  companyInfo: {
    fontSize: 12,
    fontWeight: 700,
    textTransform: "uppercase",
    color: "#FFFFFF",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
    flex: 1,
  },

  separator: {
    opacity: 0.65,
    color: "#FFFFFF",
    flexShrink: 0,
  },

  userInfo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 10,
    marginLeft: "auto",
    position: "relative",
    minWidth: 0,
    flexShrink: 0,
    pointerEvents: "auto",
    zIndex: 5100,
  },

  userTrigger: {
    height: 44,
    maxWidth: 260,
    display: "inline-flex",
    alignItems: "center",
    gap: 9,
    padding: "4px 10px 4px 5px",
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.055)",
    cursor: "pointer",
    userSelect: "none",
    outline: "none",
    color: "#FFFFFF",
    boxSizing: "border-box",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    boxShadow: "0 1px 0 rgba(255,255,255,0.08) inset",
    transition:
      "background 0.22s ease, box-shadow 0.22s ease, transform 0.18s ease, border 0.22s ease, color 0.22s ease",
  },

  userTriggerHover: {
    background: "rgba(255,255,255,0.96)",
    border: "1px solid rgba(255,255,255,0.60)",
    boxShadow:
      "0 12px 28px rgba(0,0,0,0.12), 0 1px 0 rgba(255,255,255,0.80) inset",
  },

  userTriggerOpen: {
    background: "rgba(255,255,255,0.98)",
    border: "1px solid rgba(255,255,255,0.68)",
    boxShadow:
      "0 14px 34px rgba(0,0,0,0.16), 0 1px 0 rgba(255,255,255,0.86) inset",
  },

  avatarRing: {
    width: 34,
    height: 34,
    minWidth: 34,
    minHeight: 34,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    boxSizing: "border-box",
    background: "transparent",
    boxShadow: "none",
  },

  avatar: {
    width: 32,
    height: 32,
    minWidth: 32,
    minHeight: 32,
    borderRadius: "50%",
    objectFit: "cover",
    userSelect: "none",
    background: "#0B5268",
    display: "block",
  },

  avatarFallback: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#FFFFFF",
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.04em",
    background: "linear-gradient(180deg, #0B5268 0%, #073D4E 100%)",
  },

  userTextBlock: {
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  name: {
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 13,
    fontWeight: 800,
    lineHeight: 1,
    whiteSpace: "nowrap",
    maxWidth: 160,
    overflow: "hidden",
    textOverflow: "ellipsis",
    transition: "color 0.2s ease",
  },

  chevron: {
    width: 16,
    height: 16,
    minWidth: 16,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "transform 0.18s ease, color 0.18s ease",
  },

  menu: {
    position: "absolute",
    top: "calc(100% + 12px)",
    right: 0,
    width: 326,
    borderRadius: 8,
    background: "rgba(255,255,255,0.982)",
    border: "1px solid rgba(15,23,42,0.08)",
    boxShadow:
      "0 24px 70px rgba(0,0,0,0.24), 0 1px 0 rgba(255,255,255,0.86) inset",
    overflow: "hidden",
    zIndex: 1200,
    display: "flex",
    flexDirection: "column",
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
  },

  accountHeader: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "16px 16px 13px",
    background:
      "linear-gradient(180deg, rgba(248,250,252,0.96) 0%, rgba(255,255,255,0.98) 100%)",
  },

  accountAvatarLargeWrap: {
    width: 48,
    height: 48,
    minWidth: 48,
    minHeight: 48,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    boxSizing: "border-box",
    background: "transparent",
    boxShadow: "none",
  },

  accountAvatarLarge: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    objectFit: "cover",
    background: "#0B5268",
    display: "block",
  },

  accountAvatarFallbackLarge: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#FFFFFF",
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 15,
    fontWeight: 900,
    letterSpacing: "0.04em",
    background: "linear-gradient(180deg, #0B5268 0%, #073D4E 100%)",
  },

  accountIdentity: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },

  accountName: {
    color: "#0F172A",
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 15,
    fontWeight: 900,
    lineHeight: 1.15,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  accountRole: {
    color: "#64748B",
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.2,
  },

  accountDetails: {
    padding: "10px 16px 12px",
    borderTop: "1px solid rgba(15,23,42,0.06)",
    borderBottom: "1px solid rgba(15,23,42,0.06)",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    background: "#FFFFFF",
  },

  detailRow: {
    display: "grid",
    gridTemplateColumns: "70px 1fr",
    gap: 10,
    alignItems: "start",
  },

  detailLabel: {
    color: "#94A3B8",
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 10.5,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    lineHeight: 1.35,
  },

  detailValue: {
    color: "#334155",
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 11.5,
    fontWeight: 800,
    lineHeight: 1.35,
    textTransform: "uppercase",
    overflowWrap: "anywhere",
  },

  menuActions: {
    padding: "8px",
    display: "flex",
    flexDirection: "column",
    gap: 4,
    background: "#FFFFFF",
  },

  menuActionItem: {
    width: "100%",
    minHeight: 50,
    padding: "9px 10px",
    border: "none",
    borderRadius: 12,
    background: "transparent",
    color: "#17313A",
    textAlign: "left",
    fontFamily: '"Nunito", "Inter", sans-serif',
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 10,
    transition: "background 0.16s ease, transform 0.16s ease",
  },

  actionIcon: {
    width: 31,
    height: 31,
    minWidth: 31,
    borderRadius: 10,
    background: "rgba(14,50,58,0.07)",
    color: "#0E323A",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },

  actionTitle: {
    display: "block",
    color: "#0F172A",
    fontSize: 12.5,
    fontWeight: 900,
    lineHeight: 1.2,
  },

  actionSubtitle: {
    display: "block",
    color: "#64748B",
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1.25,
    marginTop: 2,
  },

  logoutArea: {
    padding: "10px",
    background: "rgba(248,250,252,0.96)",
    borderTop: "1px solid rgba(15,23,42,0.06)",
  },

  logoutButton: {
    width: "100%",
    minHeight: 43,
    borderRadius: 12,
    border: "1px solid rgba(185,28,28,0.13)",
    background: "rgba(254,242,242,0.82)",
    color: "#991B1B",
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    transition:
      "background 0.16s ease, border-color 0.16s ease, transform 0.16s ease, opacity 0.16s ease",
  },

  logoutButtonDisabled: {
    opacity: 0.72,
    cursor: "not-allowed",
  },

  logoutIcon: {
    width: 18,
    height: 18,
    minWidth: 18,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },

  spinner: {
    width: 14,
    height: 14,
    borderRadius: "50%",
    border: "2px solid rgba(153,27,27,0.25)",
    borderTopColor: "#991B1B",
    animation: "spin 0.8s linear infinite",
  },
};

export default HeaderHome;
